/**
 * GraphQL queries and hooks — stakeholder pillar.
 * Split from graphql/queries.ts (issue #1409).
 */

import { gql } from '@apollo/client';
import { useQuery, type QueryResult } from '@apollo/client/react';
import { useAllPages } from './useAllPages';
import type { Stakeholder, StakeholderConnection, StakeholderFilter } from '../types/stakeholder';

/**
 * Fragment for Stakeholder fields
 */
export const STAKEHOLDER_FIELDS = gql`
  fragment StakeholderFields on Stakeholder {
    entityId
    entityType
    name
    description
    stakeholderType
    contactEmail
    organization
    _schemaVersion
    _createdAt
    _createdBy
    _updatedAt
    _updatedBy
    _deletedAt
  }
`;

/**
 * Query to get a single stakeholder by ID
 */
export const GET_STAKEHOLDER = gql`
  ${STAKEHOLDER_FIELDS}
  query GetStakeholder($id: ID!) {
    stakeholder(id: $id) {
      ...StakeholderFields
    }
  }
`;

/**
 * Query to list all stakeholders with filtering and pagination
 */
export const LIST_STAKEHOLDERS = gql`
  ${STAKEHOLDER_FIELDS}
  query ListStakeholders($filter: StakeholderFilter, $first: Int, $after: String) {
    stakeholders(filter: $filter, first: $first, after: $after) {
      edges {
        node {
          ...StakeholderFields
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

/**
 * Hook to get a single stakeholder
 */
export function useStakeholder(id: string): QueryResult<{ stakeholder: Stakeholder | null }> {
  return useQuery(GET_STAKEHOLDER, {
    variables: { id },
    skip: !id,
  });
}

/**
 * Hook to list stakeholders with optional filtering
 */
export function useStakeholders(
  filter?: StakeholderFilter,
  first: number = 50
): QueryResult<{ stakeholders: StakeholderConnection }> {
  return useQuery(LIST_STAKEHOLDERS, {
    variables: { filter, first },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}

/**
 * Hook to list ALL stakeholders, auto-paginating past the resolver's 100-row
 * per-request cap (see {@link useAllPages}). Used by pickers (e.g. the
 * Business Process Owner field, issue #769) that need the full catalog to
 * filter over client-side — `useStakeholders`'s single-page fetch would
 * silently truncate at 100 for a larger stakeholder catalog.
 */
export function useAllStakeholders(
  filter?: StakeholderFilter,
  first: number = 100
): QueryResult<{ stakeholders: StakeholderConnection }> {
  return useAllPages<{ stakeholders: StakeholderConnection }>(
    LIST_STAKEHOLDERS,
    (data) => data?.stakeholders,
    { filter },
    first
  );
}
